// funpanel.ts — the "mash and see results" front door. Big flavour buttons that each fire a fresh
// word tuned to a vibe and always look impressive (they force big reactions + the full dictionary,
// ignoring the right-panel sliders). A landscape rectangle below the game window: a big centered
// "START MASHING!" header over a two-cell row — juicy buttons + Export on the left, blurb on the
// right. It drops in from the top (gravity bounce) shortly after the welcome plays.

import { ensureFullDictionary } from "../core/dictionary";
import { setPersonalityBig, setReelSpeed, armShowcase } from "../juice/effects/09-personality";
import { fireShowcaseWord } from "../tuner/fire";
import { setCalm, isCalm } from "../juice/a11y";
import { setJuiceBoost } from "../tuner/scale";
import { forkRand } from "../core/rng";
import { feel } from "../juice/feel";

interface Flavor {
  id: string;
  label: string;
  cls: string;
  arm: boolean | null | undefined; // true=flirt · false=troll · null=guaranteed reel(random) · undefined=don't force
  tip: string;
}

const FLAVORS: Flavor[] = [
  { id: "flirt", label: "💗 Flirt", cls: "fun-flirt", arm: true, tip: "Fires a word whose two rare 'diva' letters flirt — a heart-throwing reel." },
  { id: "troll", label: "💨 Troll", cls: "fun-troll", arm: false, tip: "Same reel, but the divas troll each other — a puff, a recoil, a worried brow." },
  { id: "juice", label: "🎉 Big juice", cls: "fun-juice", arm: null, tip: "Cranks every effect — sound, pop, shake, pitch — for one big fire." },
  { id: "surprise", label: "🎲 Surprise", cls: "fun-surprise", arm: undefined, tip: "A random lively word — whatever the dictionary deals." },
];

// A private stream so the occasional attract wobble can't perturb the seeded gameplay rng.
const attractRand = forkRand(0xa77ac7);

/** Occasionally wobble ONE random button to draw the eye — subtle, and not too often. */
function startAttract(panel: HTMLElement): void {
  const tick = (): void => {
    const btns = [...panel.querySelectorAll<HTMLElement>(".funpanel-btn:not(.loading)")];
    if (btns.length) {
      const b = btns[Math.floor(attractRand() * btns.length)];
      b.classList.add("attract");
      window.setTimeout(() => b.classList.remove("attract"), 850);
    }
    window.setTimeout(tick, 7000 + attractRand() * 8000); // 7–15s — present, never nagging
  };
  window.setTimeout(tick, 4000 + attractRand() * 4000);
}

export function mountFunPanel(host: HTMLElement): HTMLElement {
  const panel = document.createElement("div");
  panel.className = "funpanel pre-reveal"; // hidden until the intro drops it in
  panel.innerHTML = `
    <div class="funpanel-header">🎪 Start Mashing!</div>
    <div class="funpanel-row">
      <div class="funpanel-left">
        <div class="funpanel-buttons"></div>
        <button class="funpanel-export" data-tip="Copy the current tuned feel.json — opens a text field to grab it from.">⤓ Export</button>
      </div>
      <div class="funpanel-side">
        <div class="funpanel-hint">Big, impressive fires — goof here, then tune on the pro panel ⟶</div>
        <div class="funpanel-how">
          <ul>
            <li>The rare letters are <b>divas</b>: <b>Q Z X J</b> flirt &amp; troll, <b>K V B W Y F</b> emote.</li>
            <li>Two divas in one word put on a little <b>reel</b> — stand · wind-up · fire · react · sit.</li>
            <li>Or just type a word + <kbd>↵</kbd> in the middle.</li>
          </ul>
        </div>
      </div>
    </div>
    <textarea class="funpanel-json" readonly hidden spellcheck="false"></textarea>
  `;

  const buttons = panel.querySelector<HTMLElement>(".funpanel-buttons")!;
  let loading = false;
  for (const f of FLAVORS) {
    const b = document.createElement("button");
    b.className = `funpanel-btn ${f.cls}`;
    b.textContent = f.label;
    b.setAttribute("data-tip", f.tip);
    b.addEventListener("click", async () => {
      if (loading) return;
      loading = true;
      b.classList.add("loading");
      await ensureFullDictionary(); // first mash fetches the full dictionary; then instant
      b.classList.remove("loading");
      loading = false;
      setPersonalityBig(true); // left always looks big/impressive
      setReelSpeed(1);
      const prevCalm = isCalm();
      setCalm(true); // mash = non-triggering: no whole-screen shake (restore the user's setting after)
      if (f.id === "juice") setJuiceBoost(2.4); // Big Juice: crank sound / tween / pop / pitch
      if (f.arm !== undefined) armShowcase(f.arm); // guarantee the reel (with flavour)
      fireShowcaseWord(); // effects capture intensity + calm synchronously here
      setCalm(prevCalm);
      setJuiceBoost(1);
    });
    buttons.appendChild(b);
  }

  // Export → open a copy/paste text field with the current feel.json (toggles).
  const exportBtn = panel.querySelector<HTMLButtonElement>(".funpanel-export")!;
  const json = panel.querySelector<HTMLTextAreaElement>(".funpanel-json")!;
  exportBtn.addEventListener("click", () => {
    if (json.hidden) {
      json.value = JSON.stringify(feel, null, 2);
      json.hidden = false;
      json.focus();
      json.select();
    } else {
      json.hidden = true;
    }
  });

  host.appendChild(panel);
  startAttract(panel);
  return panel; // the intro orchestrator drops it in (timed off the welcome)
}
